import React, { useMemo } from 'react';
import { AppButton, Box, Hint, Text } from '@common-fe/common-fe';

import { OrganizationTypes } from '@/common/constants';
import Permissions from '@/common/permissions';
import { Access } from '@/modules/core/components';
import { useCurrentOrganization } from '@/modules/core/hooks';
import { EmployerStatus } from '@/modules/employer/employer.constants';
import { useCurrentPlan } from '@/modules/plan/PlanSetup/hooks';

import { PlanStatuses } from '../../plan.types';
import { useEditModeStore } from '../stores';

const EMPTY_BOX_WIDTH = '400px';

interface Props {
  isTemplateMode?: boolean;
}
const ConfigureButton: React.FC<Props> = ({ isTemplateMode }) => {
  const { planStatus } = useCurrentPlan();
  const {
    observingOrganization: { type, status },
  } = useCurrentOrganization();
  const setEditMode = useEditModeStore((state) => state.setEditMode);
  const isDraftMode = useMemo(() => planStatus === PlanStatuses.DRAFT, [planStatus]);

  const isActiveOrUnderUpdate = useMemo(
    () =>
      planStatus === PlanStatuses.ACTIVE ||
      planStatus === PlanStatuses.PENDING_ACTIVE ||
      planStatus === PlanStatuses.UNDER_UPDATE,
    [planStatus]
  );

  const isTerminationStatus = useMemo(
    () =>
      status === EmployerStatus.PendingTermination ||
      status === EmployerStatus.TerminationInProgress ||
      status === EmployerStatus.Terminated,
    [status]
  );

  const isSubsidiary = useMemo(() => type === OrganizationTypes.subsidiary, [type]);

  if (isActiveOrUnderUpdate) {
    return null;
  }

  return (
    <Box direction="row" align="center" margin={{ bottom: 'spacing24' }}>
      <Box width={EMPTY_BOX_WIDTH} />
      <Box width="medium">
        {!isSubsidiary && (
          <Access
            accessRequirements={[
              {
                permission: isTemplateMode ? Permissions.PLAN_TEMPLATE_SETUP : Permissions.EDIT_DRAFT_PLAN,
              },
            ]}
          >
            <Hint
              hideHint={!isTerminationStatus}
              hintAlign="left"
              hintElement={
                <AppButton
                  onClick={() => setEditMode(true)}
                  buttonType="secondary"
                  width="100%"
                  disabled={isTerminationStatus}
                >
                  {isDraftMode ? 'Edit' : 'Configure'}
                </AppButton>
              }
            >
              <Text size="small" color="textOnColor" textAlign="center">
                Terminated organizations cannot edit plan.
              </Text>
            </Hint>
          </Access>
        )}
      </Box>
    </Box>
  );
};

export default ConfigureButton;
